import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import TalkingHeadAvatar from './TalkingHeadAvatar';
import NavRail from './NavRail';
// Mic on/off button is hidden for now (see the commented-out button below).
// import MicIcon from '@mui/icons-material/Mic';
// import MicOffIcon from '@mui/icons-material/MicOff';
import { AvatarProvider, useAvatar, AVATAR_STATUS } from './AvatarContext';

const getBackgroundUrl = () =>
  localStorage.getItem('background') || 'https://storage.googleapis.com/vicaassets/1.png';

// Persistent frame for every signed-in page: navigation rail, then the
// avatar (loaded once, never remounted between routes), then the current page. Layout rules live in App.css (.vica-agent-shell and friends).
const STATUS_LABELS = {
  [AVATAR_STATUS.idle]: 'Ready',
  [AVATAR_STATUS.listening]: 'Listening...',
  [AVATAR_STATUS.thinking]: 'Thinking...',
  [AVATAR_STATUS.speaking]: 'Speaking...',
};

function ShellFrame() {
  const {
    avatarRef,
    avatarReady,
    handleAvatarReady,
    handleAvatarLoading,
    avatarSelection,
    status,
    muted,
    setMuted,
  } = useAvatar();
  // Every time VICA is entered (after login, on reopening, or on refresh) the
  // caregiver taps once before anything happens. Browsers block audio until
  // the page has been tapped, so this guarantees the assistant can be heard.
  // The avatar keeps loading behind the screen; the page itself (and any
  // speech it starts) only appears after the tap.
  const [started, setStarted] = useState(false);
  const statusLabel = avatarReady ? STATUS_LABELS[status] || STATUS_LABELS.idle : 'Loading...';
  const statusKey = avatarReady ? status : 'loading';

  // While the mic button is hidden there is no way to unmute, so clear any
  // mute saved earlier and keep hands-free listening on. Remove this when the
  // button comes back.
  useEffect(() => {
    if (muted) {
      setMuted(false);
    }
  }, [muted, setMuted]);

  return (
    <div className="vica-agent-shell" style={{ backgroundImage: `url(${getBackgroundUrl()})` }}>
      <div className="vica-agent-overlay" />
      <NavRail />
      <div className="vica-agent-avatar" data-tour="avatar">
        <TalkingHeadAvatar
          ref={avatarRef}
          avatarSelection={avatarSelection}
          onLoading={handleAvatarLoading}
          onReady={handleAvatarReady}
        />
        <div className="vica-avatar-topbar">
          <div className={`vica-avatar-status is-${statusKey}`} role="status" aria-live="polite" data-tour="status">
            <span className="vica-avatar-status-dot" />
            <span>{statusLabel}</span>
          </div>
          {/* Mic on/off button: hidden for now. To bring it back, uncomment
              this, the Mic icon imports above and the "mute" step in Home.js
              TOUR_STEPS, and remove the auto-unmute effect above.
          <button
            type="button"
            className={`vica-mute-button ${muted ? 'is-muted' : ''}`}
            data-tour="mute"
            onClick={() => setMuted(!muted)}
            aria-pressed={muted}
            title={muted ? 'Microphone is off. Tap to turn it on.' : 'Microphone is on. Tap to mute.'}
          >
            {muted ? <MicOffIcon sx={{ fontSize: 30 }} /> : <MicIcon sx={{ fontSize: 30 }} />}
            <span>{muted ? 'Muted' : 'Mic on'}</span>
          </button>
          */}
        </div>
      </div>
      <div className="vica-agent-panel-wrap">
        {started && <Outlet />}
      </div>
      {!started && (
        <div className="vica-tour" role="dialog" aria-modal="true" aria-label="Welcome">
          <div className="vica-tour-dim" />
          <div className="vica-tour-card vica-tour-card-center">
            <h2 className="vica-tour-title">Welcome to VICA</h2>
            <p className="vica-tour-text">Tap the button below to begin.</p>
            <div className="vica-tour-actions">
              <button
                type="button"
                className="vica-btn vica-tour-btn-primary"
                onClick={() => setStarted(true)}
                autoFocus
              >
                Tap to begin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  return (
    <AvatarProvider>
      <ShellFrame />
    </AvatarProvider>
  );
}
